import type {
  NotifyOutcome,
  ScopedRule,
  ScopedRulesView,
  WatchTargetType,
} from "@shared/types/notifications";
import {
  OPEN_RECIPIENT_POLICY,
  isExternalAddress,
  type RecipientPolicy,
} from "@shared/types/notificationDomains";

/**
 * The words of the rules for chosen connections or schedules (alert routing), shared by the card
 * and the drawer. Pure, so the sentences are tested once.
 */

type ViewRule = ScopedRulesView["rules"][number];

/** "When it fails" and the others: the drawer's checkboxes, in the closed order. */
export const SCOPED_OUTCOME_LABELS = {
  failure: "When it fails",
  warning: "When it has warnings",
  success: "When it succeeds",
} as const satisfies Record<NotifyOutcome, string>;

const OUTCOME_SHORT = {
  failure: "fails",
  warning: "has warnings",
  success: "succeeds",
} as const satisfies Record<NotifyOutcome, string>;

const ORDER: readonly NotifyOutcome[] = ["failure", "warning", "success"];

/** "Fails, has warnings": the grid's When cell. */
export function whenText(outcomes: readonly NotifyOutcome[]): string {
  const words = ORDER.filter((outcome) => outcomes.includes(outcome)).map(
    (outcome) => OUTCOME_SHORT[outcome],
  );
  const text = words.join(", ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

const DELETED = { dataflow: "Deleted connection", schedule: "Deleted schedule" } as const;
const NONE_LEFT = { dataflow: "No connections left", schedule: "No schedules left" } as const;

/** "Clinical SQL, Deleted connection", or "No connections left" for a rule emptied by deletes. */
export function appliesToText(rule: Pick<ViewRule, "targetType" | "targets">): string {
  if (rule.targets.length === 0) return NONE_LEFT[rule.targetType];
  return rule.targets.map((target) => target.name ?? DELETED[rule.targetType]).join(", ");
}

/** "o•••@acme.example, Teams channel", or "No one yet". */
export function recipientsText(
  rule: Pick<ViewRule, "emails" | "teams">,
  policy: RecipientPolicy = OPEN_RECIPIENT_POLICY,
): string {
  // #1111: an External address is marked in the list whatever the tenant's setting.
  const emails = rule.emails.map((email) =>
    isExternalAddress(email, policy) ? `${email} (External)` : email,
  );
  const parts = [...emails, ...(rule.teams ? ["Teams channel"] : [])];
  return parts.length > 0 ? parts.join(", ") : "No one yet";
}

/** The list's label and its empty message by what the rule applies to. */
export const TARGET_WORDS = {
  dataflow: {
    list: "Connections",
    choose: "Choose at least one connection.",
    placeholder: "Choose connections",
  },
  schedule: {
    list: "Schedules",
    choose: "Choose at least one schedule.",
    placeholder: "Choose schedules",
  },
} as const satisfies Record<WatchTargetType, { list: string; choose: string; placeholder: string }>;

/** A saved rule as the PUT carries it (full addresses: only a writer edits). */
export function ruleBodyFrom(rule: ViewRule): ScopedRule {
  return {
    id: rule.id,
    name: rule.name,
    targetType: rule.targetType,
    targetIds: rule.targets.map((target) => target.id),
    outcomes: [...rule.outcomes],
    enabled: rule.enabled,
    emails: [...rule.emails],
    teams: rule.teams,
  };
}
